import React, { useState, useEffect } from 'react';
import Pagination from 'react-js-pagination';
import { useSelector } from 'react-redux';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
import Table from 'react-bootstrap/Table';
import { toast } from 'react-toastify';
import { useMutation, useQuery } from 'react-query';
import { DeleteSingle, Get, Post } from '@app/utils/httpProvider';
import { ASSET_API, ASSETGROUP_API, DEPARTMENT_API, ASSETSTATUS_API, BaseGatewayImage, ASSETTRANSFER_API, ASSET_ACCESSORY_API, MATERIALCATALOG_API, MAINTENANCE_EVENT_API } from '@app/utils/apiUrl.js';
import { URI_INTERNAL } from '../../utils/pathLocations';
import { createUrlWithParam, formatDateTimeMoment, isValidUrl } from '../../utils/helper';
import {
    objectShowNumber,
    messageToast,
} from '../../utils/define';
import { ConfirmModal } from '../../modules/modal/ConfirmModal';
import Select from 'react-select';
import QRCodeModal from './components/QRCodeModal';

// Hàm tính màu text tương phản dựa trên màu hex nền
const hexToLightBg = (hex) => {
    if (!hex) return { bg: '#f0f0f0', border: '#ccc', text: '#555' };
    const clean = hex.replace('#', '');
    const r = parseInt(clean.substring(0, 2), 16);
    const g = parseInt(clean.substring(2, 4), 16);
    const b = parseInt(clean.substring(4, 6), 16);
    return {
        bg: `rgba(${r},${g},${b},0.12)`,
        border: `rgba(${r},${g},${b},0.5)`,
        text: hex,
    };
};

const AssetStatusBadge = ({ statusName, statusColor }) => {
    const { bg, border, text } = hexToLightBg(statusColor);
    return (
        <span style={{
            display: 'inline-block',
            padding: '3px 11px',
            borderRadius: '999px',
            fontSize: '11px',
            fontWeight: 700,
            background: bg,
            color: text || '#555',
            border: `1px solid ${border}`,
            whiteSpace: 'nowrap',
            letterSpacing: '0.3px',
        }}>
            {statusName || '—'}
        </span>
    );
};

const AssetAvatar = ({ imagePath }) => {
    const url = imagePath ? (isValidUrl(imagePath) ? imagePath : BaseGatewayImage + imagePath) : null;
    return (
        <div style={{ width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f4f4f4', borderRadius: '4px', overflow: 'hidden' }}>
            {url ? (
                <img src={url} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
                <i className="feather icon-image text-muted" style={{ fontSize: '20px' }} />
            )}
        </div>
    );
};

const Asset = () => {
    const history = useHistory();
    const location = useLocation();
    const valueSearch = useSelector((state) => state.search.valueSearch);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [search, setSearch] = useState('');

    const [departmentId, setDepartmentId] = useState('');
    const [assetGroupId, setAssetGroupId] = useState('');
    const [assetStatusId, setAssetStatusId] = useState('');
    const [isDisposed, setIsDisposed] = useState('');

    const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
    const [idWantDelete, setIdWantDelete] = useState('');
    const [confirmDelMessage, setConfirmDelMessage] = useState('');

    const [qrModalData, setQrModalData] = useState({
        isOpen: false,
        assetCode: '',
        publicUrl: ''
    });

    // Trigger feather icon replacement after each render
    useEffect(() => {
        if (window.feather) window.feather.replace();
    });

    const handleShowQRCode = (asset) => {
        const publicUrl = `${window.location.origin}/public/asset/${asset.id}`;
        setQrModalData({
            isOpen: true,
            assetCode: asset.assetCode,
            publicUrl: publicUrl
        });
    };

    const handleCloseQRCode = () => {
        setQrModalData({ ...qrModalData, isOpen: false });
    };

    const handleClone = async (asset) => {
        try {
            // 1. Fetch full asset detail
            const detail = await Get(ASSET_API.GET_ITEM, asset.id);
            if (!detail) { toast.error('Không thể tải thông tin tài sản!'); return; }

            // 2. Fetch accessories for this asset
            const accessoryData = await Get(`${ASSET_ACCESSORY_API.GET_BY_ASSET_ID}/${asset.id}`);
            let clonedAccessories = [];

            if (accessoryData && accessoryData.length > 0) {
                const materialIds = accessoryData.map(x => x.materialCatalogId);
                const materialRes = await Post(MATERIALCATALOG_API.GET_BY_LIST_ID, null, materialIds);
                const materials = await materialRes.json();
                const materialMap = {};
                materials.forEach(m => { materialMap[m.id] = m; });

                clonedAccessories = accessoryData.map(a => ({
                    materialId: a.materialCatalogId,
                    materialCode: materialMap[a.materialCatalogId]?.materialCode || '',
                    materialName: materialMap[a.materialCatalogId]?.materialName || '',
                    specification: materialMap[a.materialCatalogId]?.specification || '',
                    unit: materialMap[a.materialCatalogId]?.unit || '',
                    quantity: a.quantity,
                    accessoryStatus: a.accessoryStatus || 'Hoạt động',
                    note: a.note || '',
                }));
            }

            // 3. Navigate to AssetAdd with cloned data
            history.push(URI_INTERNAL.ASSET_CREATE_PATH, {
                cloneData: {
                    assetName: detail.assetName,
                    assetGroupId: detail.assetGroupId,
                    currentDepartmentId: detail.currentDepartmentId,
                    serialNumber: detail.serialNumber || '',
                    model: detail.model || '',
                    manufacturer: detail.manufacturer || '',
                    country: detail.country || '',
                    powerSpec: detail.powerSpec || '',
                    purchaseDate: detail.purchaseDate || '',
                    purchaseValue: detail.purchaseValue || 0,
                },
                cloneAccessories: clonedAccessories,
            });
        } catch (err) {
            console.error(err);
            toast.error('Không thể sao chép tài sản!');
        }
    };

    const deleteFunc = useMutation((value) =>
        DeleteSingle(ASSET_API.DELETE_ITEM, value, null)
    );

    const paramUrl = new URLSearchParams(location.search.substring(1));
    if (
        paramUrl.get('page') != null &&
        parseInt(paramUrl.get('page'), 10) !== page
    ) {
        const pageResult = parseInt(paramUrl.get('page'), 10);
        setPage(pageResult);
    }
    if (
        paramUrl.get('size') != null &&
        parseInt(paramUrl.get('size'), 10) !== pageSize
    ) {
        const sizeResult = parseInt(paramUrl.get('size'), 10);
        setPageSize(sizeResult);
    }
    if (paramUrl.get('search') != null && paramUrl.get('search') !== search) {
        setSearch(paramUrl.get('search'));
    }

    const { isLoading, isError, error, data, refetch } = useQuery(
        ['assetData', valueSearch, page, pageSize, departmentId, assetGroupId, assetStatusId, isDisposed],
        () => {
            let model = {
                Keyword: valueSearch ? valueSearch : '',
                PageIndex: page,
                PageSize: pageSize
            };

            if (departmentId) model.CurrentDepartmentId = parseInt(departmentId, 10);
            if (assetGroupId) model.AssetGroupId = parseInt(assetGroupId, 10);
            if (assetStatusId) model.AssetStatusId = parseInt(assetStatusId, 10);
            if (isDisposed !== '') model.IsDisposed = isDisposed === 'true';

            replaceHistory(page, pageSize, valueSearch ? valueSearch : '');

            return Get(ASSET_API.GET_PAGING, null, model);
        }
    );

    const assetGroups = useQuery(['getAllAssetGroupsForMapping'], () =>
        Get(ASSETGROUP_API.GET_PAGING, null, { PageIndex: 1, PageSize: 10000 })
    );

    const departments = useQuery(['getAllDepartmentsForMapping'], () =>
        Get(DEPARTMENT_API.GET_PAGING, null, { PageIndex: 1, PageSize: 10000 })
    );

    const assetStatuses = useQuery(['getAllAssetStatusesForMapping'], () =>
        Get(ASSETSTATUS_API.GET_PAGING, null, { PageIndex: 1, PageSize: 10000 })
    );

    const getAssetGroupName = (id) => {
        if (assetGroups?.data?.data) {
            const group = assetGroups.data.data.find(x => x.id === id);
            return group ? group.assetGroupName : id;
        }
        return id;
    };

    const getDepartmentName = (id) => {
        if (departments?.data?.data) {
            const dept = departments.data.data.find(x => x.id === id);
            return dept ? dept.departmentName : id;
        }
        return id;
    };

    const getAssetStatusName = (id) => {
        if (assetStatuses?.data?.data) {
            const status = assetStatuses.data.data.find(x => x.id === id);
            return status ? status.statusName : id;
        }
        return id;
    };

    const getAssetStatus = (id) => {
        return assetStatuses?.data?.data?.find(x => x.id === id) || null;
    };

    // Convert hex color to rgba with given opacity
    const hexToRgba = (hex, opacity) => {
        if (!hex) return 'transparent';
        const clean = hex.replace('#', '');
        const r = parseInt(clean.substring(0, 2), 16);
        const g = parseInt(clean.substring(2, 4), 16);
        const b = parseInt(clean.substring(4, 6), 16);
        return `rgba(${r},${g},${b},${opacity})`;
    };

    const departmentOptions = departments?.data?.data?.map(item => ({ value: item.id, label: item.departmentName })) || [];
    const assetGroupOptions = assetGroups?.data?.data?.map(item => ({ value: item.id, label: item.assetGroupName })) || [];
    const assetStatusOptions = assetStatuses?.data?.data?.map(item => ({ value: item.id, label: item.statusName })) || [];
    const isDisposedOptions = [
        { value: 'true', label: 'Đã thanh lý' },
        { value: 'false', label: 'Chưa thanh lý' }
    ];

    useEffect(() => {
        refetch();
    }, [pageSize, page, departmentId, assetGroupId, assetStatusId, isDisposed]);

    const replaceHistory = async (Page, Size, Search) => {
        history.replace(
            createUrlWithParam(URI_INTERNAL.ASSET_PATH, {
                page: Page,
                size: Size,
                search: Search
            })
        );
    };

    React.useEffect(() => {
        if (valueSearch != null && valueSearch !== search) {
            replaceHistory(1, pageSize, valueSearch);
        }
    }, [valueSearch]);

    const changePage = (number) => {
        setPage(number);
        replaceHistory(number, pageSize, search);
    };

    const onChangeShowNumber = (e) => {
        setPageSize(e.target.value);
        replaceHistory(page, e.target.value, search);
    };

    const deleteAction = (id, name) => {
        setIdWantDelete(id);
        setConfirmDelMessage(name);
        setOpenConfirmDeleteModal(true);
    };

    const acceptDelete = async () => {
        if (idWantDelete !== '') {
            deleteFunc.mutate(idWantDelete, {
                onSuccess: (res) => {
                    if (res.ok || res.status === 200 || res.status === 201 || res.status === 204) {
                        refetch();
                        toast.success(messageToast.messageToastDeleteSuccess);
                        if (
                            data != null &&
                            data.data.length === 1 &&
                            page !== 1
                        ) {
                            history.push(
                                createUrlWithParam(
                                    URI_INTERNAL.ASSET_PATH,
                                    {
                                        page: page - 1,
                                        size: pageSize,
                                        search: search
                                    }
                                )
                            );
                        }
                    } else {
                        toast.error(messageToast.messageToastDeleteError);
                    }
                },
                onError: (res) => {
                    toast.error(messageToast.messageToastTryCatch);
                }
            });

            replaceHistory(page, pageSize, search);
            setOpenConfirmDeleteModal(false);
        }
    };

    const cancelDelete = () => {
        setIdWantDelete('');
        setOpenConfirmDeleteModal(false);
    };
    // Hàm xử lý chuyển trang
    const goToTransferPage = (item) => {
        history.push({
            pathname: URI_INTERNAL.ASSET_TRANSFER_PATH,
            state: {
                asset: item,
                departmentName: getDepartmentName(item.currentDepartmentId)
            }
        });
    };

    // Hàm kiểm tra API khi click
    const handleTransferClick = async (item) => {
        try {
            const resTranfer = await Get(`${ASSETTRANSFER_API.GET_BY_ASSET_ID}/${item.id}`);

            const listTransfer = resTranfer?.data || resTranfer || [];

            const isTransferring = listTransfer.some(transfer => transfer.status === 'Chờ phê duyệt' || transfer.status === 'Đã phê duyệt'); // Tương ứng public string Status 

            if (isTransferring) {
                toast.warning('Tài sản này đang trong quá trình điều chuyển!');
                return;
            } 

            const resMaintenance = await Get(`${MAINTENANCE_EVENT_API.GET_BY_ASSET_ID}/${item.id}`);
            const listMaintenance = resMaintenance?.data || resMaintenance || [];

            const isMaintaining = listMaintenance.some(
                m => m.status === 'Chờ phê duyệt' || m.status === 'Đã phê duyệt'
            );

            if (isMaintaining) {
                toast.warning('Tài sản này đang trong quá trình sửa chữa');
                return;
            }

            goToTransferPage(item);
        } catch (error) {
            goToTransferPage(item);
        }
    };
    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-lg-12">
                    <div className="card shadow-sm">
                        <div className="card-header bg-white d-flex justify-content-between align-items-center py-3">
                            <h5 className="card-title fw-bold text-primary mb-0">
                                <i className="fas fa-boxes mr-2" />
                                Danh sách tài sản
                            </h5>
                            <NavLink to={URI_INTERNAL.ASSET_CREATE_PATH} className="ml-auto">
                                <button type="button" className="btn btn-info btn-sm shadow-sm">
                                    <i className="fas fa-plus mr-1" /> Thêm tài sản mới
                                </button>
                            </NavLink>
                        </div>
                        <div className="card-body">
                            <div className="d-flex justify-content-between">
                                <div className="d-flex align-items-center mb-3 flex-wrap">
                                    <div className="d-flex align-items-center mr-3 mb-2">
                                        <label htmlFor="showNumber" className="mr-2 mb-0">Hiển thị:</label>
                                        <select
                                            id="showNumber"
                                            className="form-control"
                                            value={pageSize}
                                            onChange={(e) => onChangeShowNumber(e)}
                                            style={{ width: "auto" }}
                                        >
                                            {objectShowNumber.map((item, key) => (
                                                <option key={Number(key)} value={item.value}>
                                                    {item.text}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="d-flex align-items-center mr-3 mb-2" style={{ minWidth: "220px", flex: 1 }}>
                                        <Select
                                            options={departmentOptions}
                                            value={departmentOptions.find(opt => opt.value === departmentId) || null}
                                            onChange={(selectedOption) => setDepartmentId(selectedOption ? selectedOption.value : '')}
                                            placeholder="-- Tất cả phòng ban --"
                                            isClearable={true}
                                            styles={{ container: (provided) => ({ ...provided, width: "100%" }) }}
                                        />
                                    </div>

                                    <div className="d-flex align-items-center mr-3 mb-2" style={{ minWidth: "220px", flex: 1 }}>
                                        <Select
                                            options={assetGroupOptions}
                                            value={assetGroupOptions.find(opt => opt.value === assetGroupId) || null}
                                            onChange={(selectedOption) => setAssetGroupId(selectedOption ? selectedOption.value : '')}
                                            placeholder="-- Tất cả nhóm TS --"
                                            isClearable={true}
                                            styles={{ container: (provided) => ({ ...provided, width: "100%" }) }}
                                        />
                                    </div>

                                    <div className="d-flex align-items-center mb-2 mr-3" style={{ minWidth: "220px", flex: 1 }}>
                                        <Select
                                            options={assetStatusOptions}
                                            value={assetStatusOptions.find(opt => opt.value === assetStatusId) || null}
                                            onChange={(selectedOption) => setAssetStatusId(selectedOption ? selectedOption.value : '')}
                                            placeholder="-- Tất cả trạng thái --"
                                            isClearable={true}
                                            styles={{ container: (provided) => ({ ...provided, width: "100%" }) }}
                                        />
                                    </div>

                                    <div className="d-flex align-items-center mb-2" style={{ minWidth: "220px", flex: 1 }}>
                                        <Select
                                            options={isDisposedOptions}
                                            value={isDisposedOptions.find(opt => opt.value === isDisposed) || null}
                                            onChange={(selectedOption) => setIsDisposed(selectedOption ? selectedOption.value : '')}
                                            placeholder="-- Thanh lý --"
                                            isClearable={true}
                                            styles={{ container: (provided) => ({ ...provided, width: "100%" }) }}
                                        />
                                    </div>
                                </div>
                            </div>

                            {isLoading ? (
                                <div className="text-center p-5">Đang tải...</div>
                            ) : isError ? (
                                <div className="alert alert-danger">Có lỗi xảy ra: {error?.message}</div>
                            ) : (
                            <>
                            <div className="table-responsive">
                                <Table striped bordered hover size="sm" className="mb-0" style={{ minWidth: '1100px' }}>
                                    <thead>
                                        <tr className="text-dark fw-bold text-uppercase" style={{ fontSize: '12px' }}>
                                            <th className="text-center py-2" style={{ width: '50px' }}>STT</th>
                                            <th className="py-2" style={{ width: '60px' }}>Ảnh</th>
                                            <th className="py-2">Mã tài sản</th>
                                            <th className="py-2">Tên tài sản</th>
                                            <th className="text-center py-2" style={{ width: '130px' }}>Ngày tạo</th>
                                            <th className="py-2">Nhóm TS</th>
                                            <th className="py-2">Phòng ban</th>
                                            <th className="text-center py-2">Trạng thái</th>
                                            <th className="text-center py-2" style={{ width: '60px' }}>Đã TL</th>
                                            <th className="text-center py-2" style={{ width: '150px', whiteSpace: 'nowrap' }}>Chức năng</th>
                                        </tr>
                                    </thead>
                                    {data?.data?.length > 0 ? (
                                        <tbody>
                                            {data.data.map((item, index) => {
                                                const statusObj = getAssetStatus(item.assetStatusId);
                                                const statusColor = statusObj?.statusColor || null;
                                                return (
                                                    <tr key={item.id}>
                                                        <td className="text-center align-middle py-2">
                                                            {index + 1 + (page - 1) * pageSize}
                                                        </td>
                                                        <td className="align-middle py-2">
                                                            <AssetAvatar imagePath={item.imageThumbnail || item.imagePath} />
                                                        </td>
                                                        <td className="align-middle py-2 fw-bold">{item.assetCode}</td>
                                                        <td className="align-middle py-2">
                                                            <div>{item.assetName}</div>
                                                            {item.serialNumber && (
                                                                <div style={{ fontSize: '0.75rem', color: '#888', marginTop: 2 }}>
                                                                    # {item.serialNumber}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="text-center align-middle py-2 small">
                                                            {item.createdDate ? formatDateTimeMoment(item.createdDate) : ''}
                                                        </td>
                                                        <td className="align-middle py-2 small">{getAssetGroupName(item.assetGroupId)}</td>
                                                        <td className="align-middle py-2 small">{getDepartmentName(item.currentDepartmentId)}</td>
                                                        <td className="text-center align-middle py-2">
                                                            <AssetStatusBadge
                                                                statusName={statusObj?.statusName || item.assetStatusId}
                                                                statusColor={statusColor}
                                                            />
                                                        </td>
                                                        <td className="text-center align-middle py-2 small">{item.isDisposed ? 'Có' : 'Không'}</td>
                                                        <td className="text-center align-middle py-2">
                                                            <div className="d-flex justify-content-center align-items-center" style={{ gap: '4px' }}>
                                                                {/* Chi tiết */}
                                                                <button type="button"
                                                                    className="btn btn-xs btn-outline-primary"
                                                                    title="Chi tiết"
                                                                    style={{ width: '28px', height: '28px', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                                                    onClick={() => history.push(createUrlWithParam(URI_INTERNAL.ASSET_EDIT_PATH, { id: item.id }))}>
                                                                    <i className="fas fa-eye" style={{ fontSize: '13px' }} />
                                                                </button>
                                                                {/* Điều chuyển */}
                                                                <button type="button"
                                                                    className="btn btn-xs btn-outline-warning"
                                                                    title="Điều chuyển"
                                                                    style={{ width: '28px', height: '28px', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                                                    onClick={() => handleTransferClick(item)}>
                                                                    <i className="fas fa-exchange-alt" style={{ fontSize: '13px' }} />
                                                                </button>
                                                                {/* Sao chép */}
                                                                <button type="button"
                                                                    className="btn btn-xs btn-outline-info"
                                                                    title="Sao chép"
                                                                    style={{ width: '28px', height: '28px', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                                                    onClick={() => handleClone(item)}>
                                                                    <i className="fas fa-copy" style={{ fontSize: '13px' }} />
                                                                </button>
                                                                {/* In Mã QR */}
                                                                <button type="button"
                                                                    className="btn btn-xs btn-outline-secondary"
                                                                    title="In Mã QR"
                                                                    style={{ width: '28px', height: '28px', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                                                    onClick={() => handleShowQRCode(item)}>
                                                                    <i className="fas fa-qrcode" style={{ fontSize: '13px' }} />
                                                                </button>
                                                                {/* Xóa */}
                                                                <button type="button"
                                                                    className="btn btn-xs btn-outline-danger"
                                                                    title="Xóa"
                                                                    style={{ width: '28px', height: '28px', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                                                    onClick={() => deleteAction(item.id, item.assetName)}>
                                                                    <i className="fas fa-trash-alt" style={{ fontSize: '13px' }} />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    ) : (
                                        <tbody>
                                            <tr>
                                                <td colSpan="10" className="text-center p-4">Không có dữ liệu hiển thị</td>
                                            </tr>
                                        </tbody>
                                    )}
                                </Table>
                            </div>

                            <div className="d-flex justify-content-center mt-3">
                                <Pagination
                                    itemClass="page-item"
                                    linkClass="page-link"
                                    innerClass="pagination pagination-sm"
                                    activePage={page}
                                    itemsCountPerPage={Number(pageSize)}
                                    totalItemsCount={data?.totalRecords || 0}
                                    pageRangeDisplayed={5}
                                    onChange={changePage}
                                />
                            </div>
                            <ConfirmModal
                                isOpen={openConfirmDeleteModal}
                                confirmType="delete"
                                handleAccept={() => acceptDelete()}
                                handleCancel={() => cancelDelete()}
                                content={confirmDelMessage}
                            />
                            <QRCodeModal
                                isOpen={qrModalData.isOpen}
                                handleClose={handleCloseQRCode}
                                assetCode={qrModalData.assetCode}
                                publicUrl={qrModalData.publicUrl}
                            />
                            </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Asset;
